import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import batchLoadProducts from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import cleanupUploadData from "@insite/client-framework/Store/Components/OrderUpload/Handlers/CleanupUploadData";
import processFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/ProcessFile";
import setAllowCancel from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetAllowCancel";
import setErrorsModalIsOpen from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetErrorsModalIsOpen";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setIsUploading from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsUploading";
import setUploadCancelled from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadCancelled";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { PimUploadModel } from "@thos/Models/PimUploadModel";
import React, { FC, useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface OwnProps {
    mappings: PimUploadModel[];
    mapName: string;
    mappingSelection: SafeDictionary<string>;
    fieldOptions: string[];
    processMappingsHandler: (value: any, onComplete?: () => void) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    uploadedItems: state.components.orderUpload.uploadedItems,

});

const mapDispatchToProps = {

};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderUploadStyles {
    mainWrapper?: InjectableCss;
    orderCancellationSuccessfulText?: TypographyPresentationProps;
    uploadLinkTooltipText?: TypographyPresentationProps;
    downloadTemplateButton?: ButtonPresentationProps;
    fileUploader?: FileUploadPresentationProps;
    includeFirstRowCheckbox?: CheckboxProps;
    cancelUploadButton?: ButtonPresentationProps;
    uploadFileButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    uploadingButtonLabel?: TypographyPresentationProps;
    uploadFileButtonLabel?: TypographyPresentationProps;
    select?: SelectPresentationProps;
}

export const orderUploadStyles: OrderUploadStyles = {
    mainWrapper: {
        css: css`
            width: 100%;
        `,
    },
    orderCancellationSuccessfulText: {
        color: "warning",
        css: css`
            line-height: 25px;
        `,
    },
    uploadLinkTooltipText: {
        css: css`
            margin-bottom: 15px;
        `,
    },
    downloadTemplateButton: {
        css: css`
            margin-bottom: 15px;
        `,
        variant: "tertiary",
    },
    includeFirstRowCheckbox: {
        css: css`
            margin: 15px 0;
            display: block;
        `,
    },
    fileUploader: {
        buttonProps: {
            variant: "tertiary",
        },
    },
    cancelUploadButton: {
        variant: "secondary",
        css: css`
            float: right;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    uploadFileButton: {
        css: css`
            float: right;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 10px;
                            width: 100%;
                        `,
                        css`
                            margin-left: 15px;
                            width: initial;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin-right: 8px;
        `,
    },
    uploadingButtonLabel: {
        css: css`
            font-size: 15px;
            font-weight: bold;
            vertical-align: super;
        `,
    },
    uploadFileButtonLabel: {
        css: css`
            font-size: 15px;
            font-weight: bold;
        `,
    },
    select: {},
};

const styles = orderUploadStyles;

const MapPIMFields: FC<Props> = ({
    mappings,
    mapName,
    mappingSelection,
    fieldOptions,
    processMappingsHandler
}) => {

    const updateFieldMapping = (value: string, originatingField: string) => {
        mappingSelection[originatingField] = value;
    };

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <Typography as="p" {...styles.orderCancellationSuccessfulText}>
                {siteMessage("PimMapFields_Directions")}
            </Typography>
            <GridContainer>
                {mappings?.map(mapField => (
                    <GridItem width={12}>
                        <GridItem width={6} style={{ display: "inline-block" }}>
                            <Select
                                {...styles.select}
                                key={`cdt_${mapName}_source`}
                                label={mapName}
                                value={mapField.originatingField}
                                disabled={true}

                            >
                                <option value={`${mapField.originatingField}`} key={`${mapField.originatingField}`}>
                                    {mapField.originatingField}
                                </option>
                            </Select>
                        </GridItem>
                        <GridItem width={6} style={{ display: "inline-block" }}>
                            <Select
                                {...styles.select}
                                key={`cdt_${mapName}_destination`}
                                label={mapName}
                                value={mappingSelection[mapName]}
                                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                    updateFieldMapping(event.target.value, mapField.originatingField);
                                }}
                                data-test-selector={`cdt_${mapName}`}
                            >
                                {fieldOptions?.map(mapField => (
                                    <option value={`${mapField}`} key={`${mapField}`}>
                                        {mapField}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    </GridItem>
                ))}
            </GridContainer>

            <Button
                onClick={processMappingsHandler}
            >
                {translate("Process Mappings")}
            </Button>

        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MapPIMFields);
